import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { RefObject, WheelEvent } from "react";

interface IUseProductConfiguratorSliderImageScrollReturn {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  imageRefs: RefObject<(HTMLDivElement | null)[]>;
  handleWheel: (
    e: WheelEvent<HTMLDivElement>,
    imagesLength: number,
    setSelectedImageIndex: Dispatch<SetStateAction<number>>,
  ) => void;
}

export const useProductConfiguratorSliderImageScroll = (
  selectedImageIndex: number,
): IUseProductConfiguratorSliderImageScrollReturn => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!imageRefs.current[selectedImageIndex] || !scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const selectedElement = imageRefs.current[selectedImageIndex];

    if (!selectedElement) {
      return;
    }

    const isHorizontalScroll =
      container.scrollWidth > container.clientWidth &&
      container.scrollHeight <= container.clientHeight;

    if (isHorizontalScroll) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    } else {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [selectedImageIndex]);

  const handleWheel = (
    e: WheelEvent<HTMLDivElement>,
    imagesLength: number,
    setSelectedImageIndex: Dispatch<SetStateAction<number>>,
  ) => {
    const delta = e.deltaY;
    if (imagesLength < 2) {
      return;
    }

    if (delta > 0) {
      setSelectedImageIndex((prev) => Math.min(prev + 1, imagesLength - 1));
    } else if (delta < 0) {
      setSelectedImageIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  return {
    scrollContainerRef,
    imageRefs,
    handleWheel,
  };
};
